import React, { useState } from 'react';
import { UsersIcon, PlusIcon, TrashIcon, XIcon } from 'lucide-react';
import { useDarkClasses } from '../../../../hooks/useDarkClasses';
import { SearchWithAutocomplete, Suggestion } from '../../../../components/UI/SearchWithAutocomplete';
import { useRoleTheme } from '../../../../hooks/useRoleTheme';
import { CHARACTER_ARCHETYPES_BY_GENRE, DEFAULT_CHARACTER_ARCHETYPES, REPRESENTATION_TAGS, ALL_REPRESENTATION_TAGS } from '../genreModuleData';
interface CharacterEntry {
  id: string;
  firstName: string;
  lastName: string;
  nickname: string;
  role: string;
  culturalIdentity: string;
}
const ROLES = ['Main Character', 'Love Interest', 'Antagonist', 'Side Character'] as const;
const CULTURAL_IDENTITY_OPTIONS = ['African American', 'Afro-Caribbean', 'Afro-Latina/o', 'Asian American', 'Black British', 'Chinese', 'East African', 'East Asian', 'Filipino', 'First Nations', 'Hawaiian / Pacific Islander', 'Hispanic / Latina/o', 'Indigenous Australian', 'Indian', 'Japanese', 'Korean', 'Māori', 'Mexican American', 'Middle Eastern', 'Mixed / Multiracial', 'Native American', 'Nigerian', 'Pakistani', 'South Asian', 'Southeast Asian', 'Vietnamese', 'West African', 'West Indian', 'White / European', 'Other'] as const;
// Build suggestion lists
const culturalIdentitySuggestions: Suggestion[] = CULTURAL_IDENTITY_OPTIONS.map(ci => ({
  id: ci.toLowerCase().replace(/[\s\/]+/g, '-'),
  label: ci,
  type: 'Identity'
}));
interface CharactersRepresentationSectionProps {
  bookForm?: any;
  setBookForm?: (next: any) => void;
  dk?: ReturnType<typeof useDarkClasses>;
}
export const CharactersRepresentationSection: React.FC<CharactersRepresentationSectionProps> = ({
  bookForm,
  setBookForm,
  dk: dkProp
}) => {
  const rt = useRoleTheme();
  const dkLocal = useDarkClasses();
  const dk = dkProp ?? dkLocal;
  const primaryGenre: string = bookForm?.primaryGenre || bookForm?.genre || (Array.isArray(bookForm?.genres) ? bookForm.genres[0] : '') || '';
  // ─── Characters state ───
  const [characters, setCharacters] = useState<CharacterEntry[]>([{
    id: crypto.randomUUID?.() || '1',
    firstName: '',
    lastName: '',
    nickname: '',
    role: 'Main Character',
    culturalIdentity: ''
  }]);
  const [culturalSearches, setCulturalSearches] = useState<Record<string, string>>({});
  const addCharacter = () => {
    if (characters.length >= 6) return;
    setCharacters(prev => [...prev, {
      id: crypto.randomUUID?.() || String(Date.now()),
      firstName: '',
      lastName: '',
      nickname: '',
      role: 'Main Character',
      culturalIdentity: ''
    }]);
  };
  const removeCharacter = (id: string) => {
    if (characters.length <= 1) return;
    setCharacters(prev => prev.filter(c => c.id !== id));
  };
  const updateCharacter = (id: string, field: keyof CharacterEntry, value: string) => {
    setCharacters(prev => prev.map(c => c.id === id ? {
      ...c,
      [field]: value
    } : c));
  };
  // ─── Archetypes state ───
  const archetypeOptions = CHARACTER_ARCHETYPES_BY_GENRE[primaryGenre] || DEFAULT_CHARACTER_ARCHETYPES;
  const [selectedArchetypes, setSelectedArchetypes] = useState<string[]>([]);
  const [archetypeSearch, setArchetypeSearch] = useState('');
  const archetypeSuggestions: Suggestion[] = archetypeOptions.filter(a => !selectedArchetypes.includes(a)).map(a => ({
    id: a.toLowerCase().replace(/\s+/g, '-'),
    label: a,
    type: 'Archetype'
  }));
  // ─── Representation state ───
  const [selectedRep, setSelectedRep] = useState<string[]>([]);
  const [repSearch, setRepSearch] = useState('');
  const repSuggestions: Suggestion[] = ALL_REPRESENTATION_TAGS.filter(r => !selectedRep.includes(r)).map(r => ({
    id: r.toLowerCase().replace(/\s+/g, '-'),
    label: r,
    type: 'Representation'
  }));
  const ACCENT = 'bg-indigo-100 text-indigo-700 border-indigo-500 dark:bg-indigo-900/30 dark:text-indigo-300';
  return <div className={`p-6 rounded-xl shadow-sm ${dk.card}`} data-id="element-20480" data-mp="1hh57a43gbwh2">
      <h2 className={`text-lg font-bold mb-2 flex items-center ${dk.textHeading}`} data-id="element-20481" data-mp="1hiq2zx1i5lgi">
        <UsersIcon className="w-5 h-5 mr-2 text-indigo-500" data-id="element-20482" data-mp="1dho118g7avau" />
        Characters & Representation
      </h2>
      <p className={`text-xs mb-6 ${dk.textMuted}`} data-id="element-20483" data-mp="kqmhkv5h6dli">
        Add main characters, their archetypes, and representation tags to help
        readers find books that resonate with them.
      </p>

      {/* ─── Main Characters ─── */}
      <div className="mb-8" data-id="element-20484" data-mp="tr1e6qnw3f4b">
        <label className={`block text-sm font-bold mb-3 ${dk.textPrimary}`} data-id="element-20485" data-mp="tprha4srwcz9">
          Main Characters
        </label>
        <div className="space-y-3" data-id="element-20486" data-mp="2a7njbum5l86m">
          {characters.map((char, idx) => <div key={char.id} className={`p-4 rounded-lg border ${dk.border} ${dk.surfaceMuted}`} data-id="element-20487" data-mp="2567yo3nw13or" data-mpi={idx}>
              <div className="flex items-center justify-between mb-3" data-id="element-20488" data-mp="2hotvair6gbgx">
                <span className={`text-xs font-semibold ${dk.textMuted}`} data-id="element-20489" data-mp="2hmz8eywci9a7">
                  Character {idx + 1}
                </span>
                {characters.length > 1 && <button onClick={() => removeCharacter(char.id)} className="text-red-500 hover:text-red-700 transition-colors p-1" aria-label="Remove character" data-id="element-20490" data-mp="28ox8kmoclmoi">
                    <TrashIcon className="w-3.5 h-3.5" data-id="element-20491" data-mp="2mog5skfmfdm1" />
                  </button>}
              </div>
              {/* Row 1: Names — 3 columns on desktop */}
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3" data-id="element-20492" data-mp="2rkwtxemtlrz4">
                <div data-id="element-20493" data-mp="3fb9d5lninb50">
                  <label className={`block text-xs mb-1 ${dk.textMuted}`} data-id="element-20494" data-mp="3far9i3yixun2">
                    First Name
                  </label>
                  <input type="text" value={char.firstName} onChange={e => updateCharacter(char.id, 'firstName', e.target.value)} placeholder="e.g. Elara" className={`w-full p-2 rounded-md border text-sm ${dk.inputOnCard}`} data-id="element-20495" data-mp="3u02msn69fjk4" />
                </div>
                <div data-id="element-20496" data-mp="3b0yfbiud9xuk">
                  <label className={`block text-xs mb-1 ${dk.textMuted}`} data-id="element-20497" data-mp="3b1gkj4z0bmem">
                    Last Name
                  </label>
                  <input type="text" value={char.lastName} onChange={e => updateCharacter(char.id, 'lastName', e.target.value)} placeholder="e.g. Voss" className={`w-full p-2 rounded-md border text-sm ${dk.inputOnCard}`} data-id="element-20498" data-mp="367vmxe1bsufy" />
                </div>
                <div data-id="element-20499" data-mp="2ndglzivgerph">
                  <label className={`block text-xs mb-1 ${dk.textMuted}`} data-id="element-20500" data-mp="12tl6bv4338na">
                    Nickname / Alias
                  </label>
                  <input type="text" value={char.nickname} onChange={e => updateCharacter(char.id, 'nickname', e.target.value)} placeholder="e.g. Ghost, Blade" className={`w-full p-2 rounded-md border text-sm ${dk.inputOnCard}`} data-id="element-20501" data-mp="17qttyb8044vx" />
                </div>
              </div>
              {/* Row 2: Role + Cultural Identity — 2 columns on tablet+ */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-3" data-id="element-20502" data-mp="t1xkqau7kltq">
                <div data-id="element-20503" data-mp="xv0eaiyzjv9x">
                  <label className={`block text-xs mb-1 ${dk.textMuted}`} data-id="element-20504" data-mp="xv9vpi5ysu40">
                    Role
                  </label>
                  <select value={char.role} onChange={e => updateCharacter(char.id, 'role', e.target.value)} className={`w-full p-2 rounded-md border text-sm ${dk.inputOnCard}`} data-id="element-20505" data-mp="cqk96b71m5u">
                    {ROLES.map(role => <option key={role} value={role} data-id="element-20506" data-mp="33xh7htc1id62">
                        {role}
                      </option>)}
                  </select>
                </div>
                <div data-id="element-20507" data-mp="3dttw4csqa0ty">
                  <label className={`block text-xs mb-1 ${dk.textMuted}`} data-id="element-20508" data-mp="3dt0yq2fnsnoh">
                    Cultural Identity
                  </label>
                  {char.culturalIdentity ? <div className="flex items-center gap-2" data-id="element-20509" data-mp="3njdgxo0044rg">
                      <span className={`inline-flex items-center px-2.5 py-1.5 ${rt.badgeRadius} text-xs font-medium ${dk.darkMode ? 'bg-indigo-900/50 text-indigo-300' : 'bg-indigo-100 text-indigo-900'}`} data-id="element-20510" data-mp="3ilkbix6ives8">
                        {char.culturalIdentity}
                        <button onClick={() => updateCharacter(char.id, 'culturalIdentity', '')} className="ml-1.5 hover:opacity-75" data-id="element-20511" data-mp="f2nwth31r78v">
                          <XIcon className="w-3 h-3" data-id="element-20512" data-mp="63spu2s6ht4i" />
                        </button>
                      </span>
                    </div> : <SearchWithAutocomplete value={culturalSearches[char.id] || ''} onChange={v => setCulturalSearches(prev => ({
                ...prev,
                [char.id]: v
              }))} suggestions={culturalIdentitySuggestions} placeholder="e.g. African American, South Asian..." onSelect={item => {
                updateCharacter(char.id, 'culturalIdentity', item.label);
                setCulturalSearches(prev => ({
                  ...prev,
                  [char.id]: ''
                }));
              }} onSearch={val => {
                updateCharacter(char.id, 'culturalIdentity', val);
                setCulturalSearches(prev => ({
                  ...prev,
                  [char.id]: ''
                }));
              }} showIcon={false} data-id="element-20513" data-mp="3sg9dfd86fb57" />}
                </div>
              </div>
            </div>)}
        </div>
        {characters.length < 6 && <button onClick={addCharacter} className={`mt-3 flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium border transition-colors ${dk.surfaceMuted} ${dk.textSecondary} ${dk.border} hover:border-indigo-400`} data-id="element-20514" data-mp="2rnw53nhopvwa">
            <PlusIcon className="w-4 h-4" data-id="element-20515" data-mp="3k6jkqndwwipz" />
            Add Character
          </button>}
      </div>

      {/* ─── Character Archetypes ─── */}
      <div className="mb-8" data-id="element-20516" data-mp="3pgvm8cfsqpio">
        <label className={`block text-sm font-bold mb-1 ${dk.textPrimary}`} data-id="element-20517" data-mp="3phoq95ndf0x4">
          Character Archetypes
        </label>
        <p className={`text-xs mb-2 ${dk.textMuted}`} data-id="element-20518" data-mp="24unv3xj4opi">
          {primaryGenre ? `Showing archetypes for ${primaryGenre}` : 'Select a genre to see genre-specific archetypes'}
        </p>
        <SearchWithAutocomplete value={archetypeSearch} onChange={setArchetypeSearch} suggestions={archetypeSuggestions} placeholder="e.g. Brooding Hero, Ice Queen, Morally Grey..." onSelect={item => {
        setSelectedArchetypes(prev => prev.includes(item.label) ? prev : [...prev, item.label]);
        setArchetypeSearch('');
      }} data-id="element-20519" data-mp="36jcdzb15xaxb" />
        {selectedArchetypes.length > 0 && <div className="flex flex-wrap gap-1.5 mt-2" data-id="element-20520" data-mp="12u7xll9mqs4r">
            {selectedArchetypes.map(archetype => <span key={archetype} className={`inline-flex items-center px-2.5 py-0.5 ${rt.badgeRadius} text-xs font-medium ${ACCENT}`} data-id="element-20521" data-mp="16vjijdhk6p6m">
                {archetype}
                <button onClick={() => setSelectedArchetypes(prev => prev.filter(x => x !== archetype))} className="ml-1.5 hover:opacity-75" data-id="element-20522" data-mp="1gu8yv1tt41kr">
                  <XIcon className="w-3 h-3" data-id="element-20523" data-mp="t4odyde3eu8u" />
                </button>
              </span>)}
          </div>}
      </div>

      {/* ─── Representation Tags ─── */}
      <div data-id="element-20524" data-mp="fqx8epj55i7">
        <label className={`block text-sm font-bold mb-1 ${dk.textPrimary}`} data-id="element-20525" data-mp="g0mg02r38mu">
          Representation Tags
        </label>
        <p className={`text-xs mb-2 ${dk.textMuted}`} data-id="element-20526" data-mp="5e2a6yb4fw4n">
          Help readers find books with characters and stories that reflect their
          experiences.
        </p>
        <SearchWithAutocomplete value={repSearch} onChange={setRepSearch} suggestions={repSuggestions} placeholder="e.g. LGBTQ+, Neurodivergent, BIPOC, Disability..." onSelect={item => {
        setSelectedRep(prev => prev.includes(item.label) ? prev : [...prev, item.label]);
        setRepSearch('');
      }} data-id="element-20527" data-mp="3p1vnpkbf7hk0" />
        {selectedRep.length > 0 && <div className="flex flex-wrap gap-1.5 mt-2" data-id="element-20528" data-mp="369jq1avpp4g7">
            {selectedRep.map(tag => <span key={tag} className={`inline-flex items-center px-2.5 py-0.5 ${rt.badgeRadius} text-xs font-medium ${ACCENT}`} data-id="element-20529" data-mp="31cjfonrtxfja">
                {tag}
                <button onClick={() => setSelectedRep(prev => prev.filter(x => x !== tag))} className="ml-1.5 hover:opacity-75" data-id="element-20530" data-mp="2rdc1xzh6nivn">
                  <XIcon className="w-3 h-3" data-id="element-20531" data-mp="3c58zlbtqoryk" />
                </button>
              </span>)}
          </div>}
      </div>
    </div>;
};